import React, { useCallback, useEffect, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Link, useLocation, useParams } from "react-router-dom";
import { CreditCard, Download, Eye, Loader2, Printer, RefreshCcw, Send, X } from "lucide-react";
import { api, API_BASE_URL } from "../../lib/axios";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import {
  CommercialInvoicePreview,
  type CommercialInvoicePreviewDto,
} from "./CommercialInvoicePreview";
import { factureDocumentState } from "./factureDocumentPresentation";
import { printHtmlDocument } from "../../utils/printDocument";

type InvoiceStatus = "DRAFT" | "ISSUED" | "PARTIALLY_PAID" | "PAID" | "CANCELLED" | "ARCHIVED";
type InvoiceDocumentStatus = "READY" | "GENERATING" | "FAILED" | "MISSING";
type PaymentMethod = "CASH" | "BANQUE" | "MOBILE_MONEY";

type InvoiceReceiptDto = {
  id: number;
  number: string;
  url: string | null;
  paiementId: number | null;
  issuedAt: string | null;
  title: "QUITTANCE DE PAIEMENT" | "REÇU D’ACOMPTE";
};

type InvoicePaymentDto = {
  id: number;
  amount: number;
  currency: string;
  method: string;
  status: string;
  paidAt: string | null;
  reference: string | null;
  banque: string | null;
  transactionNumber: string | null;
  note: string | null;
  recordedBy: { id: number; name: string; email: string | null } | null;
  receipt: InvoiceReceiptDto | null;
};

type LinkedPassageDto = {
  id: number;
  passage: string;
  type: string | null;
  plannedAt: string | null;
  executedAt: string | null;
  equipe: { id: number; name: string } | null;
  tournee: { id: number; number: string } | null;
  terrainStatus: string;
  status: string;
  billing: string;
};

type InvoiceDetailDto = {
  id: number;
  number: string;
  status: InvoiceStatus;
  devise: string;
  issuedAt: string | null;
  dueDate: string | null;
  billingPeriod: string | null;
  periodeDebut: string | null;
  periodeFin: string | null;
  totalHt: number;
  taxAmount: number;
  totalTtc: number;
  paidAmount: number;
  remainingBalance: number;
  cancelReason: string | null;
  cancelledAt: string | null;
  cancelledBy: {
    id: number;
    nom: string;
    prenom: string | null;
    email: string | null;
  } | null;
  client: {
    id: number;
    name: string;
    type: string | null;
    phone: string | null;
    email: string | null;
    reference: string | null;
    zone: string | null;
    nom?: string | null;
    prenom?: string | null;
    nomEntreprise?: string | null;
  } | null;
  contract: {
    id: number;
    plan: string | null;
    frequency: string;
    status: string;
    price: number;
    currency: string;
    plannedPassages: number | null;
    realizedPassages: number;
  } | null;
  lines: Array<{ id: number; description: string; quantity: number; unitPrice: number; total: number }>;
  linkedPassages: LinkedPassageDto[];
  payments: InvoicePaymentDto[];
  receipts: InvoiceReceiptDto[];
  timeline: Array<{
    id: string | number;
    type: string;
    label: string;
    at: string | null;
    actor: { id: number | null; name: string } | null;
  }>;
  paiementsCount: number;
  document: {
    status: InvoiceDocumentStatus;
    url: string | null;
    fileSize: number | null;
  };
};

type PaymentForm = {
  amount: string;
  paidAt: string;
  method: PaymentMethod;
  reference: string;
  bank: string;
  transactionNumber: string;
  note: string;
};

function absoluteUploadUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const root = API_BASE_URL.replace(/\/api\/?$/i, "");
  return root ? `${root}${path.startsWith("/") ? path : `/${path}`}` : path;
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "—";
  const u = ["o", "Ko", "Mo", "Go"];
  let i = 0;
  let x = n;
  while (x >= 1024 && i < u.length - 1) {
    x /= 1024;
    i++;
  }
  return `${x >= 10 || i === 0 ? Math.round(x) : x.toFixed(1)} ${u[i]}`;
}

function money(value: number, currency: string): string {
  return `${value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;
}

function statusLabel(status: InvoiceStatus): string {
  const labels: Record<InvoiceStatus, string> = {
    DRAFT: "Brouillon",
    ISSUED: "Impayée",
    PARTIALLY_PAID: "Partiellement payée",
    PAID: "Payée",
    CANCELLED: "Annulée",
    ARCHIVED: "Archivée",
  };
  return labels[status];
}

function dateLabel(value?: string | null): string {
  return value ? dayjs(value).format("DD/MM/YYYY") : "—";
}

function monthLabel(value?: string | null): string {
  return value ? dayjs(`${value}-01`).format("MMMM YYYY") : "—";
}

export default function FactureDetailStubPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const listeFacturesPath = location.pathname.startsWith("/admin/")
    ? "/admin/finance/factures"
    : "/finance/factures";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [facture, setFacture] = useState<InvoiceDetailDto | null>(null);
  const [sendLoading, setSendLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [commercialPreview, setCommercialPreview] = useState<CommercialInvoicePreviewDto | null>(null);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentForm, setPaymentForm] = useState<PaymentForm>({
    amount: "",
    paidAt: dayjs().format("YYYY-MM-DD"),
    method: "CASH",
    reference: "",
    bank: "",
    transactionNumber: "",
    note: "",
  });

  const load = useCallback(async () => {
    if (!id || Number.isNaN(Number(id))) {
      setError("Identifiant invalide.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get<{ success?: boolean; data?: InvoiceDetailDto }>(
        `finance/factures/${id}/detail`
      );
      if (!data?.success || !data.data) {
        setError("Facture introuvable.");
        setFacture(null);
        return;
      }
      setFacture(data.data);
      setPaymentForm((prev) => ({ ...prev, amount: String(data.data?.remainingBalance ?? "") }));
    } catch {
      setError("Impossible de charger la facture.");
      setFacture(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSendToClient = useCallback(async () => {
    if (!id) return;
    setSendLoading(true);
    try {
      await api.post(`/finance/factures/${id}/send-to-client`);
      toast.success("Facture envoyée au client.");
    } catch {
      toast.error("Impossible d’envoyer la facture au client.");
    } finally {
      setSendLoading(false);
    }
  }, [id]);

  const handlePrepareDocument = useCallback(async () => {
    if (!id) return;
    try {
      await api.post(`/factures/${id}/generate-pdf`);
      toast.success("Document en préparation.");
      await load();
    } catch {
      toast.error("Impossible de préparer le document.");
    }
  }, [id, load]);

  const loadCommercialPreview = useCallback(async (): Promise<CommercialInvoicePreviewDto | null> => {
    if (!id) return null;
    try {
      const { data } = await api.get<{ success?: boolean; data?: CommercialInvoicePreviewDto }>(
        `/factures/${id}/commercial-preview`
      );
      if (!data?.success || !data.data) {
        toast.error("Aperçu facture indisponible.");
        return null;
      }
      return data.data;
    } catch {
      toast.error("Impossible de charger l’aperçu facture.");
      return null;
    }
  }, [id]);

  const openCommercialPreview = useCallback(async () => {
    setPreviewLoading(true);
    try {
      const preview = await loadCommercialPreview();
      if (!preview) return;
      setCommercialPreview(preview);
      setPreviewOpen(true);
    } finally {
      setPreviewLoading(false);
    }
  }, [loadCommercialPreview]);

  const printCommercialPreview = useCallback(async (preview?: CommercialInvoicePreviewDto | null) => {
    try {
      const data = preview ?? commercialPreview ?? await loadCommercialPreview();
      if (!data) return;
      const html = renderToStaticMarkup(<CommercialInvoicePreview data={data} />);
      printHtmlDocument(html, `Facture ${data.facture.numero}`);
    } catch {
      toast.error("Impossible d’imprimer le document facture.");
    }
  }, [commercialPreview, loadCommercialPreview]);

  const canPay = facture?.status === "ISSUED" || facture?.status === "PARTIALLY_PAID";

  const validatePayment = useCallback((): string | null => {
    if (!facture) return "Facture introuvable.";
    const amount = Number(paymentForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) return "Le montant doit être supérieur à 0.";
    if (amount > facture.remainingBalance + 0.0001) return "Le montant dépasse le solde restant.";
    if (!paymentForm.paidAt) return "La date de paiement est obligatoire.";
    if (paymentForm.method === "BANQUE" && !paymentForm.bank.trim()) return "La banque est obligatoire.";
    if (paymentForm.method === "BANQUE" && !paymentForm.reference.trim()) return "La référence bancaire est obligatoire.";
    if (paymentForm.method === "MOBILE_MONEY" && !paymentForm.transactionNumber.trim()) {
      return "Le numéro de transaction est obligatoire.";
    }
    return null;
  }, [facture, paymentForm]);

  const submitPayment = useCallback(async () => {
    const validation = validatePayment();
    if (validation) {
      toast.error(validation);
      return;
    }
    if (!facture) return;
    setPaymentLoading(true);
    try {
      await api.post(`/finance/factures/${facture.id}/paiements`, {
        montantPaye: Number(paymentForm.amount),
        modePaiement: paymentForm.method,
        referencePaiement: paymentForm.reference || paymentForm.transactionNumber || null,
        banque: paymentForm.bank || null,
        numeroTransaction: paymentForm.transactionNumber || null,
        datePaiement: paymentForm.paidAt,
        commentaire: paymentForm.note || null,
      });
      toast.success("Paiement enregistré. Reçu/quittance disponible.");
      setPaymentOpen(false);
      await load();
    } catch (err: unknown) {
      const message =
        typeof err === "object" &&
        err !== null &&
        "response" in err &&
        typeof (err as { response?: { data?: { message?: string } } }).response?.data?.message === "string"
          ? (err as { response: { data: { message: string } } }).response.data.message
          : "Impossible d’enregistrer le paiement.";
      toast.error(message);
    } finally {
      setPaymentLoading(false);
    }
  }, [facture, load, paymentForm, validatePayment]);

  if (loading) {
    return (
      <div className="flex min-h-[280px] items-center justify-center bg-slate-950 text-slate-400">
        <Loader2 className="h-8 w-8 animate-spin" aria-hidden />
      </div>
    );
  }

  if (error || !facture) {
    return (
      <div className="min-h-screen bg-slate-950 px-6 py-10 text-slate-100">
        <div className="mx-auto max-w-lg rounded-2xl border border-slate-800 bg-slate-900/80 p-8">
          <p className="text-red-400">{error ?? "Facture introuvable."}</p>
          <Link
            to={listeFacturesPath}
            className="mt-6 inline-flex rounded-xl bg-slate-700 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-600"
          >
            Retour aux factures
          </Link>
        </div>
      </div>
    );
  }

  const documentState = factureDocumentState({ numero: facture.number, document: facture.document });
  const documentUrl = absoluteUploadUrl(facture.document.url);
  const payments = facture.payments ?? [];
  const receipts = facture.receipts ?? [];
  const latestPayment = payments[0] ?? null;
  const receiptActions = receipts.length > 0 ? receipts : payments.flatMap((p) => (p.receipt ? [p.receipt] : []));

  return (
    <div className="min-h-screen bg-slate-950 px-6 py-10 text-slate-100">
      {paymentOpen ? (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-2xl rounded-2xl border border-slate-700 bg-slate-900 p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-xl font-bold text-white">Enregistrer un paiement</h2>
                <p className="mt-1 text-sm text-slate-400">Solde restant : {money(facture.remainingBalance, facture.devise)}</p>
              </div>
              <button type="button" onClick={() => setPaymentOpen(false)} className="rounded-lg p-2 text-slate-400 hover:bg-slate-800">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <Field label="Montant payé">
                <input value={paymentForm.amount} onChange={(e) => setPaymentForm((p) => ({ ...p, amount: e.target.value }))} type="number" min="0" step="0.01" className="input" />
              </Field>
              <Field label="Date paiement">
                <input value={paymentForm.paidAt} onChange={(e) => setPaymentForm((p) => ({ ...p, paidAt: e.target.value }))} type="date" className="input" />
              </Field>
              <Field label="Mode paiement">
                <select value={paymentForm.method} onChange={(e) => setPaymentForm((p) => ({ ...p, method: e.target.value as PaymentMethod }))} className="input">
                  <option value="CASH">CASH</option>
                  <option value="BANQUE">BANQUE</option>
                  <option value="MOBILE_MONEY">MOBILE_MONEY</option>
                </select>
              </Field>
              <Field label="Référence paiement">
                <input value={paymentForm.reference} onChange={(e) => setPaymentForm((p) => ({ ...p, reference: e.target.value }))} className="input" />
              </Field>
              <Field label="Banque">
                <input value={paymentForm.bank} onChange={(e) => setPaymentForm((p) => ({ ...p, bank: e.target.value }))} className="input" />
              </Field>
              <Field label="Numéro transaction">
                <input value={paymentForm.transactionNumber} onChange={(e) => setPaymentForm((p) => ({ ...p, transactionNumber: e.target.value }))} className="input" />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Note interne">
                  <textarea value={paymentForm.note} onChange={(e) => setPaymentForm((p) => ({ ...p, note: e.target.value }))} rows={3} className="input" />
                </Field>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button type="button" onClick={() => setPaymentOpen(false)} className="rounded-xl border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800">Annuler</button>
              <button type="button" disabled={paymentLoading} onClick={() => void submitPayment()} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-50">
                {paymentLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                Enregistrer paiement
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {previewOpen && commercialPreview ? (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-black/80 p-4">
          <div className="sticky top-0 z-[101] mx-auto mb-4 flex max-w-[980px] flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-700 bg-slate-950/95 px-4 py-3 shadow-xl">
            <div>
              <p className="text-sm font-semibold text-white">Aperçu facture commerciale</p>
              <p className="text-xs text-slate-400">Données réelles de la facture {commercialPreview.facture.numero}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              {commercialPreview.pdf.url ? (
                <a
                  href={absoluteUploadUrl(commercialPreview.pdf.url) ?? "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-500"
                >
                  <Download className="h-4 w-4" />
                  Télécharger PDF
                </a>
              ) : null}
              <button
                type="button"
                onClick={() => void printCommercialPreview(commercialPreview)}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
              >
                <Printer className="h-4 w-4" />
                Imprimer
              </button>
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
              >
                <X className="h-4 w-4" />
                Fermer
              </button>
            </div>
          </div>
          <CommercialInvoicePreview data={commercialPreview} />
        </div>
      ) : null}

      <div className="mx-auto max-w-7xl space-y-6">
        <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-6 shadow-xl">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-sky-400/90">Finance · Détail facture</p>
              <h1 className="mt-2 text-3xl font-black text-white">{facture.number}</h1>
              <p className="mt-2 text-sm text-slate-400">Client : <span className="font-semibold text-slate-100">{facture.client?.name ?? "—"}</span></p>
            </div>
            <div className="grid gap-3 text-sm sm:grid-cols-2 lg:min-w-[520px]">
              <Metric label="Statut" value={statusLabel(facture.status)} />
              <Metric label="TTC" value={money(facture.totalTtc, facture.devise)} accent="text-amber-300" />
              <Metric label="Payé" value={money(facture.paidAmount, facture.devise)} accent="text-emerald-300" />
              <Metric label="Solde" value={money(facture.remainingBalance, facture.devise)} accent={facture.remainingBalance > 0 ? "text-rose-300" : "text-emerald-300"} />
              <Metric label="Émission" value={dateLabel(facture.issuedAt)} />
              <Metric label="Échéance" value={dateLabel(facture.dueDate)} />
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5">
          <div className="flex flex-wrap gap-3">
            <Link to={listeFacturesPath} className="inline-flex rounded-xl border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800">
              Retour aux factures
            </Link>
          <button
            type="button"
            disabled={previewLoading}
            onClick={() => void openCommercialPreview()}
            className="inline-flex items-center gap-2 rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 disabled:opacity-50"
          >
            {previewLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
            Voir facture
          </button>
            {documentUrl ? (
              <a href={documentUrl} download className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500">
                <Download className="h-4 w-4" />
                Télécharger PDF
              </a>
            ) : null}
            <button
              type="button"
              onClick={() => void printCommercialPreview()}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-600 px-3 py-2 text-sm font-medium text-slate-100 hover:bg-slate-800 disabled:opacity-50"
            >
              <Printer className="h-4 w-4" />
              Imprimer
            </button>
            {canPay ? (
              <button type="button" onClick={() => setPaymentOpen(true)} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500">
                <CreditCard className="h-4 w-4" />
                Payer
              </button>
            ) : null}
            <button
              type="button"
              disabled={sendLoading || !documentState.available}
              onClick={() => void handleSendToClient()}
              className="inline-flex items-center gap-2 rounded-lg border border-sky-500/60 px-3 py-2 text-sm font-medium text-sky-100 hover:bg-sky-950/40 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sendLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Envoyer au client
            </button>
            {facture.client ? <Link to={`/admin/clients/${facture.client.id}`} className="inline-flex rounded-xl border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800">Voir client</Link> : null}
            {facture.contract ? <Link to="/admin/contrats" className="inline-flex rounded-xl border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800">Voir contrat</Link> : null}
            {receiptActions.slice(0, 1).map((receipt) => (
              <a key={receipt.id} href={`${API_BASE_URL}/paiements/${receipt.paiementId ?? receipt.id}/pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-emerald-500/60 px-4 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-950/30">
                <Eye className="h-4 w-4" />
                Voir quittance / reçu
              </a>
            ))}
          </div>
        </section>

        <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
          <Section title="Résumé financier">
            <div className="grid gap-3 sm:grid-cols-2">
              <Metric label="Total HT" value={money(facture.totalHt, facture.devise)} />
              <Metric label="TVA" value={money(facture.taxAmount, facture.devise)} />
              <Metric label="Total TTC" value={money(facture.totalTtc, facture.devise)} accent="text-amber-300" />
              <Metric label="Montant payé" value={money(facture.paidAmount, facture.devise)} accent="text-emerald-300" />
              <Metric label="Solde restant" value={money(facture.remainingBalance, facture.devise)} accent={facture.remainingBalance > 0 ? "text-rose-300" : "text-emerald-300"} />
              <Metric label="Statut paiement" value={statusLabel(facture.status)} />
              <Metric label="Nombre de paiements" value={String(payments.length)} />
              <Metric label="Dernier paiement" value={latestPayment ? dateLabel(latestPayment.paidAt) : "—"} />
            </div>
          </Section>

          <Section title="Client">
            {facture.client ? (
              <div className="space-y-3 text-sm">
                <Info label="Nom client" value={facture.client.name} />
                <Info label="Type client" value={facture.client.type ?? "—"} />
                <Info label="Téléphone" value={facture.client.phone ?? "—"} />
                <Info label="E-mail" value={facture.client.email ?? "—"} />
                <Info label="Référence client" value={facture.client.reference ?? "—"} />
                <Info label="Zone" value={facture.client.zone ?? "—"} />
                <Link to={`/admin/clients/${facture.client.id}`} className="inline-flex rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500">Voir fiche client</Link>
              </div>
            ) : <Empty text="Client non renseigné." />}
          </Section>
        </div>

        <Section title="Contrat / abonnement">
          {facture.contract ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Metric label="Plan" value={facture.contract.plan ?? "—"} />
              <Metric label="Fréquence" value={facture.contract.frequency} />
              <Metric label="Période facturée" value={monthLabel(facture.billingPeriod)} />
              <Metric label="Prix abonnement" value={money(facture.contract.price, facture.contract.currency)} />
              <Metric label="Passages prévus" value={String(facture.contract.plannedPassages ?? "—")} />
              <Metric label="Passages réalisés" value={String(facture.contract.realizedPassages)} />
              <Metric label="Statut contrat" value={facture.contract.status} />
              <Link to="/admin/contrats" className="inline-flex items-center justify-center rounded-xl border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-100 hover:bg-slate-800">Voir contrat</Link>
            </div>
          ) : <Empty text="Aucun contrat lié à cette facture." />}
        </Section>

        <Section title="Passages liés">
          {facture.linkedPassages.length ? (
            <Table headers={["Passage", "Type", "Date prévue", "Date exécution", "Équipe", "Tournée", "Statut terrain", "Facturation"]}>
              {facture.linkedPassages.map((p) => (
                <tr key={p.id} className="border-b border-slate-800">
                  <Td>{p.passage}</Td>
                  <Td>{p.type ?? "—"}</Td>
                  <Td>{dateLabel(p.plannedAt)}</Td>
                  <Td>{dateLabel(p.executedAt)}</Td>
                  <Td>{p.equipe?.name ?? "—"}</Td>
                  <Td>{p.tournee?.number ?? "—"}</Td>
                  <Td>{p.terrainStatus}</Td>
                  <Td>{p.billing}</Td>
                </tr>
              ))}
            </Table>
          ) : <Empty text="Aucun passage lié à cette facture." />}
        </Section>

        <Section title="Paiements">
          {payments.length ? (
            <Table headers={["Date paiement", "Montant", "Mode", "Référence", "Banque / transaction", "Reçu / quittance", "Enregistré par"]}>
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-slate-800">
                  <Td>{dateLabel(p.paidAt)}</Td>
                  <Td>{money(p.amount, p.currency)}</Td>
                  <Td>{p.method}</Td>
                  <Td>{p.reference ?? "—"}</Td>
                  <Td>{[p.banque, p.transactionNumber].filter(Boolean).join(" · ") || "—"}</Td>
                  <Td>{p.receipt ? <a href={`${API_BASE_URL}/paiements/${p.id}/pdf`} target="_blank" rel="noopener noreferrer" className="text-emerald-300 hover:text-emerald-200">{p.receipt.title}</a> : "—"}</Td>
                  <Td>{p.recordedBy?.name ?? "—"}</Td>
                </tr>
              ))}
            </Table>
          ) : <Empty text="Aucun paiement enregistré." />}
        </Section>

        <Section title="Document">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ring-1 ${documentState.status === "READY" ? "bg-emerald-500/15 text-emerald-200 ring-emerald-500/35" : documentState.status === "FAILED" ? "bg-rose-500/15 text-rose-200 ring-rose-500/35" : "bg-amber-500/15 text-amber-100 ring-amber-500/35"}`}>
                {documentState.label}
              </span>
              <p className="mt-3 text-sm text-slate-400">{documentState.message}</p>
              <p className="mt-2 text-xs text-slate-500">Taille fichier : {facture.document.fileSize ? formatBytes(facture.document.fileSize) : "—"}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              {documentUrl ? <a href={documentUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-500"><Eye className="h-4 w-4" />Voir</a> : null}
              {documentUrl ? <a href={documentUrl} download className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-500"><Download className="h-4 w-4" />Télécharger</a> : null}
              <button type="button" onClick={() => void printCommercialPreview()} className="inline-flex items-center gap-2 rounded-lg border border-slate-600 px-3 py-2 text-sm font-medium text-slate-100 hover:bg-slate-800"><Printer className="h-4 w-4" />Imprimer</button>
              {documentState.status === "FAILED" || documentState.status === "MISSING" ? (
                <button type="button" onClick={() => void handlePrepareDocument()} className="inline-flex items-center gap-2 rounded-lg border border-amber-500/60 px-3 py-2 text-sm font-medium text-amber-100 hover:bg-amber-950/30"><RefreshCcw className="h-4 w-4" />{documentState.status === "FAILED" ? "Régénérer document" : "Préparer document"}</button>
              ) : null}
            </div>
          </div>
        </Section>

        <Section title="Timeline">
          {facture.timeline.length ? (
            <ol className="space-y-3">
              {facture.timeline.map((event) => (
                <li key={event.id} className="rounded-xl border border-slate-800 bg-slate-950/40 p-4">
                  <p className="font-semibold text-white">{event.label}</p>
                  <p className="mt-1 text-xs text-slate-500">{dateLabel(event.at)} · {event.actor?.name ?? "Système"}</p>
                </li>
              ))}
            </ol>
          ) : <Empty text="Aucun événement d’historique disponible." />}
        </Section>

        {facture.status === "CANCELLED" && (
          <div className="rounded-xl border border-rose-900/50 bg-rose-950/30 p-4 text-sm">
            <p className="font-semibold text-rose-300">Facture annulée</p>
            {facture.cancelledAt && (
              <p className="mt-2 text-slate-300">
                Date : {dayjs(facture.cancelledAt).format("DD/MM/YYYY HH:mm")}
              </p>
            )}
            {facture.cancelReason && (
              <p className="mt-2 text-slate-300">
                Motif : <span className="text-white">{facture.cancelReason}</span>
              </p>
            )}
            {facture.cancelledBy && (
              <p className="mt-2 text-slate-400">
                Par : {facture.cancelledBy.prenom ? `${facture.cancelledBy.prenom} ` : ""}
                {facture.cancelledBy.nom}
                {facture.cancelledBy.email ? ` · ${facture.cancelledBy.email}` : ""}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-xl">
      <h2 className="mb-4 text-lg font-bold text-white">{title}</h2>
      {children}
    </section>
  );
}

function Metric({ label, value, accent = "text-white" }: { label: string; value: string; accent?: string }): React.JSX.Element {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`mt-1 font-semibold ${accent}`}>{value}</p>
    </div>
  );
}

function Info({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-800 pb-2">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-100">{value}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <label className="block text-sm">
      <span className="mb-1 block text-slate-300">{label}</span>
      {children}
    </label>
  );
}

function Table({ headers, children }: { headers: string[]; children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-left text-sm">
        <thead>
          <tr className="border-y border-slate-700 bg-slate-950/70 text-xs uppercase tracking-wide text-slate-400">
            {headers.map((header) => <th key={header} className="px-3 py-3">{header}</th>)}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

function Td({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <td className="px-3 py-3 text-slate-200">{children}</td>;
}

function Empty({ text }: { text: string }): React.JSX.Element {
  return <p className="rounded-xl border border-dashed border-slate-700 bg-slate-950/40 p-4 text-sm text-slate-400">{text}</p>;
}
